import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight, Phone } from "lucide-react";
import { EASE } from "@/lib/motion";
import { scrollToTarget } from "@/components/SmoothScroll";

const IMG = (id) => `${id}?auto=format&fit=crop&w=1600&q=80`;

const SLIDES = [
  {
    tag: "Cabinet de consultance",
    title: ["L'expertise qui structure", "et fait grandir", "votre organisation"],
    text: "Ressources humaines, comptabilité, paie, informatisation et fiscalité : un seul partenaire pour piloter votre entreprise ou votre institution avec rigueur.",
    cta: { label: "Demander un devis", href: "#contact" },
    img: IMG("https://images.unsplash.com/photo-1739302750702-e26a61113758"),
    first: true,
  },
  {
    tag: "Ressources humaines",
    title: ["Des équipes bien organisées,", "un personnel valorisé"],
    text: "Organisation, recrutement, contrats et politiques RH. Avec notre solution GRH & PAIE, suivez vos salariés et votre paie en toute conformité.",
    cta: { label: "Découvrir la solution", href: "#boussole" },
    img: IMG("https://images.unsplash.com/photo-1758519289200-384c7ef2d163"),
  },
  {
    tag: "Gestion externalisée",
    title: ["Confiez-nous la gestion", "de vos RH et de votre paie"],
    text: "Nous prenons en charge, pour vous, l'administration du personnel, la paie et les déclarations sociales : vous gagnez du temps, vous réduisez les risques d'erreur et vous gardez la maîtrise de votre activité.",
    cta: { label: "Demander un devis", href: "#contact" },
    img: IMG("https://images.unsplash.com/photo-1758519288548-046187014c85"),
  },
  {
    tag: "Nos tarifs",
    title: ["Une offre claire, un tarif", "mensuel selon votre taille"],
    text: "Assistance en gestion externe de la paie et des ressources humaines : un expert RH et un expert paie de KORBAN CONSULTING à vos côtés.",
    cta: { label: "Voir le détail des tarifs", href: "#tarifs" },
    flyer: true,
  },
  {
    tag: "Comptabilité & paie",
    title: ["Des comptes fiables", "et une paie sans erreur"],
    text: "Tenue de la comptabilité, états financiers, bulletins de paie et déclarations sociales : des chiffres clairs pour décider en confiance.",
    cta: { label: "Nous contacter", href: "#contact" },
    img: IMG("https://images.unsplash.com/photo-1707902665498-a202981fb5ac"),
  },
  {
    tag: "Informatisation",
    title: ["Des outils numériques", "adaptés à votre structure"],
    text: "Nous informatisons entreprises et institutions : choix des solutions, mise en place, formation et accompagnement pour gagner en efficacité.",
    cta: { label: "Parlons de votre projet", href: "#contact" },
    img: IMG("https://images.unsplash.com/photo-1629904853716-f0bc54eea481"),
  },
  {
    tag: "Assistance fiscale",
    title: ["La conformité fiscale,", "en toute sérénité"],
    text: "Déclarations, conseils, optimisation dans le respect de la loi et accompagnement lors des contrôles : sécurisez votre situation fiscale.",
    cta: { label: "Prendre rendez-vous", href: "#contact" },
    img: IMG("https://images.unsplash.com/photo-1554224155-6726b3ff858f"),
  },
];

const FLYER_ROWS = [
  ["1 à 10 employés", "50 000"],
  ["11 à 20 employés", "75 000"],
  ["21 à 35 employés", "100 000"],
];

function Flyer() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, rotate: 1.5 }}
      animate={{ opacity: 1, y: 0, rotate: 0 }}
      transition={{ duration: 0.9, ease: EASE, delay: 0.25 }}
      className="w-full max-w-[420px] overflow-hidden rounded-2xl border-[3px] border-korban-gold bg-white text-korban-navy shadow-[0_30px_60px_rgba(0,0,0,0.45)]"
      data-testid="hero-flyer-card"
    >
      <div className="bg-gradient-to-br from-korban-navy2 to-korban-navy px-6 py-5 text-center">
        <p className="text-[0.8rem] font-bold uppercase tracking-[0.2em] text-korban-gold">Korban Consulting</p>
        <p className="mt-2 font-display text-[1.02rem] leading-snug text-white">
          Assistance en gestion externe de la paie & des ressources humaines
        </p>
      </div>
      {FLYER_ROWS.map(([who, amt], i) => (
        <div key={who} className={`flex items-center justify-between px-6 py-4 ${i < 2 ? "border-b border-slate-200" : ""}`}>
          <span className="text-[0.95rem] font-semibold">{who}</span>
          <span className="whitespace-nowrap font-display text-2xl font-bold text-[#0f6f96]">
            {amt} <small className="font-sans text-[0.62rem] font-semibold text-slate-500">FCFA TTC/mois</small>
          </span>
        </div>
      ))}
      <div className="bg-[#fff6e3] px-6 py-3.5 text-center">
        <p className="text-[0.85rem] font-bold">Un expert RH + un expert paie inclus</p>
        <p className="mt-1 inline-flex items-center gap-1.5 text-[0.85rem] font-semibold text-[#0f6f96]">
          <Phone size={13} /> 07 69 54 54 69
        </p>
      </div>
    </motion.div>
  );
}

function SlideContent({ slide }) {
  const TitleTag = slide.first ? "h1" : "h2";
  return (
    <div>
      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="text-[0.72rem] font-bold uppercase tracking-[0.28em] text-korban-gold sm:text-xs"
      >
        {slide.tag}
      </motion.p>
      <TitleTag key={slide.tag} className="mt-4 font-display font-bold tracking-tight text-white">
        {slide.title.map((line, i) => (
          <span key={line} className="block overflow-hidden pb-0.5">
            <motion.span
              className="block text-[1.7rem] leading-[1.12] sm:text-4xl lg:text-[3.2rem]"
              initial={{ y: "112%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.85, ease: EASE, delay: 0.08 + i * 0.1 }}
            >
              {line}
            </motion.span>
          </span>
        ))}
      </TitleTag>
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE, delay: 0.42 }}
        className="mt-5 max-w-xl text-[0.98rem] leading-relaxed text-slate-300 sm:text-lg"
      >
        {slide.text}
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE, delay: 0.55 }}
      >
        <a
          href={slide.cta.href}
          onClick={(e) => {
            e.preventDefault();
            scrollToTarget(slide.cta.href);
          }}
          data-testid="hero-slide-cta"
          className="mt-8 inline-flex items-center gap-2 rounded-md bg-korban-gold px-7 py-3.5 font-bold text-korban-navy shadow-[0_10px_30px_rgba(249,178,51,0.3)] transition-all hover:-translate-y-0.5 hover:bg-korban-golddark"
        >
          {slide.cta.label} <ArrowRight size={17} />
        </a>
      </motion.div>
    </div>
  );
}

export default function Slideshow({ imgOverrides = {}, textOverrides = {} }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.9], [1, 0.25]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), 6500);
    return () => clearInterval(t);
  }, [paused]);

  const base = SLIDES[index];
  const ov = textOverrides || {};
  const customTitle = ov[`slide_${index + 1}_title`];
  const slide = {
    ...base,
    img: imgOverrides?.[`slide_${index + 1}`] || base.img,
    tag: ov[`slide_${index + 1}_tag`] || base.tag,
    title: customTitle ? customTitle.split("\n").filter((l) => l.trim()) : base.title,
    text: ov[`slide_${index + 1}_text`] || base.text,
  };

  return (
    <section
      ref={heroRef}
      className="relative flex min-h-[640px] items-center overflow-hidden bg-korban-navy"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carrousel"
      data-testid="hero-slideshow"
    >
      {/* background photo + overlay */}
      <motion.div style={{ y: yImg, opacity: fade }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          {slide.img && (
            <motion.div
              key={slide.img}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.1, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <img
                src={slide.img}
                alt=""
                className="animate-kenburns h-full w-full object-cover"
                loading={index === 0 ? "eager" : "lazy"}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-korban-navy via-korban-navy/92 to-korban-navy2/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-korban-navy via-transparent to-korban-navy/60" />
      </motion.div>

      {/* giant watermark number */}
      <AnimatePresence mode="wait">
        <motion.span
          key={index}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 0.07, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="pointer-events-none absolute right-2 top-16 select-none font-display text-[9rem] font-black leading-none text-white sm:right-10 sm:text-[15rem]"
        >
          {String(index + 1).padStart(2, "0")}
        </motion.span>
      </AnimatePresence>

      <div className="relative z-10 mx-auto grid w-full max-w-6xl gap-10 px-5 pb-24 pt-32 sm:pt-36 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-14">
        <AnimatePresence mode="wait">
          <motion.div key={index} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.45 }}>
            <SlideContent slide={slide} />
          </motion.div>
        </AnimatePresence>

        <div className="relative hidden items-center justify-center lg:flex">
          {slide.flyer ? (
            <Flyer />
          ) : (
            <motion.div
              key={`img-${index}`}
              initial={{ opacity: 0, y: 34, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
              className="relative w-full max-w-[460px]"
            >
              <div className="absolute -inset-3 rounded-3xl border border-korban-gold/30" />
              <div className="absolute -bottom-5 -right-5 h-24 w-24 rounded-2xl bg-korban-gold/90" />
              <img
                src={slide.img}
                alt={slide.tag}
                className="relative aspect-[4/3.1] w-full rounded-2xl object-cover shadow-[0_30px_60px_rgba(0,0,0,0.45)]"
                loading={index === 0 ? "eager" : "lazy"}
              />
              <div className="absolute -left-5 -top-5 rounded-xl bg-korban-navy2/90 px-4 py-2.5 backdrop-blur-sm">
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-korban-gold">Korban Consulting</p>
              </div>
            </motion.div>
          )}
        </div>

        {slide.flyer && (
          <div className="lg:hidden">
            <Flyer />
          </div>
        )}
      </div>

      {/* controls */}
      <button
        onClick={() => setIndex((index - 1 + SLIDES.length) % SLIDES.length)}
        aria-label="Précédent"
        data-testid="hero-prev-arrow"
        className="absolute left-4 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-korban-navy/60 text-white backdrop-blur transition-all hover:border-korban-gold hover:text-korban-gold md:flex"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={() => setIndex((index + 1) % SLIDES.length)}
        aria-label="Suivant"
        data-testid="hero-next-arrow"
        className="absolute right-4 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-korban-navy/60 text-white backdrop-blur transition-all hover:border-korban-gold hover:text-korban-gold md:flex"
      >
        <ChevronRight size={20} />
      </button>

      <div className="absolute inset-x-0 bottom-8 z-20 flex flex-col items-center gap-3">
        <div className="flex items-center gap-2.5">
          {SLIDES.map((s, i) => (
            <button
              key={s.tag}
              onClick={() => setIndex(i)}
              aria-label={`Diapositive ${i + 1}`}
              data-testid={`hero-dot-${i + 1}`}
              className={`h-2.5 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-korban-gold" : "w-2.5 bg-white/35 hover:bg-white/60"}`}
            />
          ))}
        </div>
        <div className="h-0.5 w-40 overflow-hidden rounded-full bg-white/15">
          {!paused && <div key={index} className="animate-slideprogress h-full bg-korban-gold" />}
        </div>
      </div>
    </section>
  );
}
